import * as React from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";
import { ICompany } from "../../../types/company";
import companyService from "../../../service/company.service";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import ProgressButton from "./ProgressButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Checkbox } from "@mui/material";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { uniqBy } from "lodash";
import {
  avatarString,
  highLightWords,
  stringToColor,
} from "../../../helpers/utils";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import SimpleSearchInput from "./SimpleSearchInput";

const filter = createFilterOptions<ICompany>();

const COMPANY_PICKER_MODAL_THRESHOLD = 5;

function eventIsFromAutocompleteClear(ev: React.SyntheticEvent) {
  const ne = ev.nativeEvent as unknown as {
    composedPath?: () => EventTarget[];
  };
  const path = ne?.composedPath?.();
  if (path) {
    for (const n of path) {
      if (
        n instanceof HTMLElement &&
        n.classList?.contains("MuiAutocomplete-clearIndicator")
      ) {
        return true;
      }
    }
  }
  return (
    (ev.target as HTMLElement | null)?.closest?.(
      ".MuiAutocomplete-clearIndicator"
    ) != null
  );
}

interface ICompanyWithPath extends ICompany {
  path: string[];
}

type CompanyTreeNode = {
  company: ICompanyWithPath;
  children: CompanyTreeNode[];
};

function computeCompanyPaths(companies: ICompany[]): Map<string, string[]> {
  const companiesMap: Record<string, ICompany> = {};
  companies.forEach((c) => {
    if (c._id) companiesMap[c._id] = c;
  });
  const getTreeParent = (company: ICompany): ICompany | undefined => {
    if (company.parentCompanyId && companiesMap[company.parentCompanyId]) {
      return companiesMap[company.parentCompanyId];
    }
    if (company.vendorCompanyId && companiesMap[company.vendorCompanyId]) {
      return companiesMap[company.vendorCompanyId];
    }
    return undefined;
  };
  const paths: Record<string, string[]> = {};
  companies.forEach((c) => {
    if (c._id) paths[c._id] = [c._id];
  });
  const maxIterations = companies.length;
  for (let i = 0; i < maxIterations; i++) {
    let changed = false;
    companies.forEach((company) => {
      if (!company._id) return;
      const parent = getTreeParent(company);
      if (!parent?._id) return;
      const parentPath = paths[parent._id];
      if (!parentPath?.length) return;
      const newPath = [...parentPath, company._id];
      const cur = paths[company._id];
      if (
        cur.length !== newPath.length ||
        cur[cur.length - 1] !== company._id
      ) {
        paths[company._id] = newPath;
        changed = true;
      }
    });
    if (!changed) break;
  }
  return new Map(Object.entries(paths));
}

/** Count of direct clients (vendorCompanyId) + subsidiaries (parentCompanyId) per company id. */
function computeClientSubsidiaryScoreMap(companies: ICompany[]): Map<string, number> {
  const scores = new Map<string, number>();
  companies.forEach((c) => {
    if (c._id) scores.set(c._id, 0);
  });
  companies.forEach((c) => {
    if (c.vendorCompanyId && scores.has(c.vendorCompanyId)) {
      scores.set(
        c.vendorCompanyId,
        (scores.get(c.vendorCompanyId) || 0) + 1
      );
    }
    if (c.parentCompanyId && scores.has(c.parentCompanyId)) {
      scores.set(
        c.parentCompanyId,
        (scores.get(c.parentCompanyId) || 0) + 1
      );
    }
  });
  return scores;
}

function compareCompaniesByClientSubsidiaryScore(
  a: ICompany,
  b: ICompany,
  scoreMap: Map<string, number>
) {
  const sa = a._id ? scoreMap.get(a._id) ?? 0 : 0;
  const sb = b._id ? scoreMap.get(b._id) ?? 0 : 0;
  if (sb !== sa) return sb - sa;
  return (a.name || "").localeCompare(b.name || "", undefined, {
    sensitivity: "base",
  });
}

function buildCompanyTree(
  items: ICompanyWithPath[],
  scoreMap: Map<string, number>
): CompanyTreeNode[] {
  const nodeMap = new Map<string, CompanyTreeNode>();
  const roots: CompanyTreeNode[] = [];

  items.forEach((company) => {
    if (!company._id) return;
    nodeMap.set(company._id, { company, children: [] });
  });

  items.forEach((company) => {
    if (!company._id) return;
    const path = company.path || [];
    const parentId = path.length > 1 ? path[path.length - 2] : null;
    const node = nodeMap.get(company._id);
    if (!node) return;
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortSiblings = (a: CompanyTreeNode, b: CompanyTreeNode) =>
    compareCompaniesByClientSubsidiaryScore(
      a.company,
      b.company,
      scoreMap
    );
  roots.sort(sortSiblings);
  const sortRecursive = (node: CompanyTreeNode) => {
    node.children.sort(sortSiblings);
    node.children.forEach(sortRecursive);
  };
  roots.forEach(sortRecursive);

  return roots;
}

export default function CompanyAutoComplete({
  value,
  onChange,
  fullWidth = false,
  label,
  py,
  definedVendor,
  onlyDirectPartners,
  limitedOptions,
  enabled,
  isClientAutoComplete,
  onlyLab,
  disabledIds,
}: {
  value?: ICompany;
  definedVendor?: ICompany;
  onlyDirectPartners?: boolean;
  onChange?: (selected: ICompany | null) => void;
  fullWidth?: boolean;
  label?: string;
  py?: number;
  limitedOptions?: ICompany[];
  enabled?: boolean;
  isClientAutoComplete?: boolean;
  onlyLab?: boolean;
  disabledIds?: string[];
}) {
  const { t } = useTranslation();
  const [open, toggleOpen] = React.useState(false);
  const { state } = useAppContext();
  const { loggedInUserInfo } = state;
  const [options, setOptions] = React.useState<ICompany[]>(
    limitedOptions || []
  );
  const [isNewCompanyMyClient, setIsNewCompanyMyClient] = React.useState(
    isClientAutoComplete ||
      ![UserRoles.adminStaff, UserRoles.superadmin].includes(
        loggedInUserInfo?.role || UserRoles.user
      )
  );

  const fetchCompanyShallowList = async () => {
    if (limitedOptions) return;
    const result = [UserRoles.adminStaff, UserRoles.superadmin].includes(
      loggedInUserInfo?.role || UserRoles.user
    )
      ? await companyService.fetchCompanyShallowList()
      : await companyService.getMyClients();
    const filteredResult = (
      definedVendor
        ? result.data?.filter(
            (c: ICompany) => definedVendor?._id == c.vendorCompanyId
          ) || []
        : onlyDirectPartners
        ? result.data?.filter((c: ICompany) => !c.vendorCompanyId) || []
        : result.data || []
    ).filter((c: ICompany) => (!onlyLab || !c.vendorCompanyId) && (!disabledIds || !disabledIds.includes(c._id || "")));
    setOptions(filteredResult);
  };
  const [creatingNewCompany, setCreatingNewCompany] = React.useState(false);
  const [dialogValue, setDialogValue] = React.useState<ICompany>({ name: "" });
  const [pickerModalOpen, setPickerModalOpen] = React.useState(false);
  const [modalSearchTerm, setModalSearchTerm] = React.useState("");
  const [pickerExpandedIds, setPickerExpandedIds] = React.useState<string[]>(
    []
  );
  const [inputValue, setInputValue] = React.useState("");
  const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);

  const handleClose = () => {
    setDialogValue({ name: "" });
    toggleOpen(false);
  };

  const handleSubmit = async () => {
    setCreatingNewCompany(true);
    const result = await companyService.addNewCompany({
      ...dialogValue,
      vendorCompanyId: isNewCompanyMyClient
        ? [UserRoles.adminStaff, UserRoles.superadmin].includes(
            loggedInUserInfo?.role || UserRoles.user
          ) && definedVendor?._id
          ? definedVendor?._id
          : loggedInUserInfo?.company?._id
        : undefined,
    });
    setOptions([...options, result.data]);
    setCreatingNewCompany(false);
    onChange && onChange(result.data);
    handleClose();
  };

  const isAdmin =
    loggedInUserInfo?.role &&
    [UserRoles.superadmin, UserRoles.adminStaff].includes(
      loggedInUserInfo?.role as UserRoles
    );

  const { orderedOptions, optionDepthMap } = React.useMemo(() => {
    const validOptions = (options || []).filter((o) => o?._id && o._id !== "new");
    const scoreMap = computeClientSubsidiaryScoreMap(validOptions);
    const byId = new Map<string, ICompany>();
    validOptions.forEach((o) => byId.set(o._id as string, o));

    const childrenMap = new Map<string, ICompany[]>();
    const roots: ICompany[] = [];

    validOptions.forEach((company) => {
      const parentId = company.parentCompanyId;
      if (parentId && byId.has(parentId)) {
        const siblings = childrenMap.get(parentId) || [];
        siblings.push(company);
        childrenMap.set(parentId, siblings);
      } else {
        roots.push(company);
      }
    });

    const sortSiblings = (a: ICompany, b: ICompany) =>
      compareCompaniesByClientSubsidiaryScore(a, b, scoreMap);

    roots.sort(sortSiblings);
    childrenMap.forEach((children) => children.sort(sortSiblings));

    const depthMap = new Map<string, number>();
    const flattened: ICompany[] = [];
    const visited = new Set<string>();

    const dfs = (company: ICompany, depth: number) => {
      if (!company?._id || visited.has(company._id)) return;
      visited.add(company._id);
      depthMap.set(company._id, depth);
      flattened.push(company);
      (childrenMap.get(company._id) || []).forEach((child) => dfs(child, depth + 1));
    };

    roots.forEach((root) => dfs(root, 0));

    // Add any remaining nodes (e.g., cyclic/malformed parent pointers) in stable order.
    validOptions
      .filter((o) => o._id && !visited.has(o._id))
      .sort(sortSiblings)
      .forEach((o) => dfs(o, 0));

    return { orderedOptions: flattened, optionDepthMap: depthMap };
  }, [options]);

  const useModalPicker =
    orderedOptions.length > COMPANY_PICKER_MODAL_THRESHOLD;

  const pickerCompaniesWithPath = React.useMemo(() => {
    const validOptions = (options || []).filter(
      (o) => o?._id && o._id !== "new"
    );
    const paths = computeCompanyPaths(validOptions);
    return validOptions.map((c) => ({
      ...c,
      path: paths.get(c._id!) || [c._id!],
    }));
  }, [options]);

  const pickerTreeData = React.useMemo(() => {
    const searchTerm = modalSearchTerm;
    const companies = pickerCompaniesWithPath;

    const matchingCompanies = companies.filter(
      (c) =>
        !searchTerm ||
        c.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
    );

    const parentsOfMatchingCompanies = companies.filter((candidate) =>
      matchingCompanies.some((mc) => {
        return (
          mc.vendorCompanyId === candidate._id ||
          mc.parentCompanyId === candidate._id ||
          (mc.parentCompanyId &&
            companies.find(
              (p) =>
                p._id === mc.parentCompanyId &&
                p.vendorCompanyId === candidate._id
            ))
        );
      })
    );

    const filtered = uniqBy(
      [...matchingCompanies, ...parentsOfMatchingCompanies],
      (c) => c._id
    );

    const scoreMap = computeClientSubsidiaryScoreMap(pickerCompaniesWithPath);
    return buildCompanyTree(filtered, scoreMap);
  }, [pickerCompaniesWithPath, modalSearchTerm]);

  React.useEffect(() => {
    const term = modalSearchTerm.trim();
    if (!term) {
      setPickerExpandedIds([]);
      return;
    }
    const ids: string[] = [];
    const collectParents = (nodes: CompanyTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0 && node.company._id) {
          ids.push(node.company._id);
          collectParents(node.children);
        }
      });
    };
    collectParents(pickerTreeData);
    setPickerExpandedIds(ids);
  }, [modalSearchTerm, pickerTreeData]);

  React.useEffect(() => {
    setInputValue(value?.name || "");
  }, [value?.name, value?._id]);

  const handleClosePickerModal = () => {
    setPickerModalOpen(false);
    setModalSearchTerm("");
    setPickerExpandedIds([]);
    setInputValue(value?.name || "");
  };

  const togglePickerExpand = (id: string) => {
    setPickerExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const renderPickerRow = (node: CompanyTreeNode, depth = 0) => {
    const { company, children } = node;
    if (!company._id) return null;

    const isExpanded = pickerExpandedIds.includes(company._id);
    const hasChildren = children.length > 0;
    const rowDisabled = disabledIds?.includes(company._id || "");

    return (
      <React.Fragment key={company._id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            py: 1,
            px: 1,
            borderBottom: "1px solid rgba(0,0,0,0.04)",
            pl: 2 + depth * 5,
            cursor: hasChildren ? "pointer" : "default",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
            position: "relative",
          }}
          onClick={() =>
            hasChildren && togglePickerExpand(company._id!)
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePickerExpand(company._id!);
                }}
                sx={{ mr: 1 }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMore />}
              </IconButton>
            ) : (
              <Box sx={{ width: 32, mr: 1 }} />
            )}

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ minWidth: "50px" }}>
                {company.logoUrl ? (
                  <Box
                    component="img"
                    src={company.logoUrl}
                    alt=""
                    sx={{ maxHeight: 30, maxWidth: 50, flexShrink: 0 }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: stringToColor(company.name || ""),
                      flexShrink: 0,
                    }}
                  >
                    {avatarString(company.name || "")}
                  </Avatar>
                )}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>
                  {highLightWords(company.name || "", [modalSearchTerm])}
                </Typography>
                {company.address ? (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {company.address}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flexShrink: 0, pl: 1 }}>
            <Button
              sx={{borderWidth:"2px !important"}}
              size="small"
              variant="outlined"
              disabled={rowDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (rowDisabled) return;
                onChange && onChange(company);
                setPickerModalOpen(false);
                setModalSearchTerm("");
                setInputValue(company.name || "");
              }}
            >
              {t("Select")}
            </Button>
          </Box>
        </Box>

        {hasChildren && isExpanded ? (
          <>
            {children.map((child) => renderPickerRow(child, depth + 1))}
          </>
        ) : null}
      </React.Fragment>
    );
  };

  React.useEffect(() => {
    if (isAdmin || (definedVendor && isClientAutoComplete)) fetchCompanyShallowList();
  }, [definedVendor, isClientAutoComplete]);
  return (
    <>
      <Autocomplete
        PopperComponent={(props) => (
          <Popper
            {...props}
            style={{
              ...props.style,
              width: "auto",
              minWidth: props.style?.width,
            }}
            placement="bottom-start"
          />
        )}
        freeSolo
        disabled={enabled ? false : !isAdmin}
        value={value || null}
        open={useModalPicker ? false : autocompleteOpen}
        onOpen={(event) => {
          if (useModalPicker) {
            if (eventIsFromAutocompleteClear(event)) {
              return;
            }
            setModalSearchTerm(inputValue);
            setPickerModalOpen(true);
          } else {
            setAutocompleteOpen(true);
          }
        }}
        onClose={() => setAutocompleteOpen(false)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (reason === "clear" || reason === "reset") {
            setPickerModalOpen(false);
            return;
          }
          if (
            orderedOptions.length > COMPANY_PICKER_MODAL_THRESHOLD &&
            reason === "input"
          ) {
            setModalSearchTerm(newInputValue);
            if (newInputValue.length > 0) {
              setPickerModalOpen(true);
            }
          }
        }}
        clearOnBlur={!useModalPicker}
        onChange={(event, newValue) => {
          setPickerModalOpen(false);
          if (typeof newValue === "string") {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                name: newValue,
              });
            });
          } else if (newValue && newValue._id == "new") {
            toggleOpen(true);
            setDialogValue({
              name: newValue.name?.replaceAll('"', "").replaceAll("Add ", ""),
            });
          } else {
            onChange && onChange(newValue);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (params.inputValue !== "") {
            filtered.push({
              _id: "new",
              name: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        getOptionLabel={(option) => {
          // for example value selected with enter, right from the input
          if (typeof option === "string") {
            return option;
          }
          if (option && option._id == "new") {
            return "new";
          }
          return option.name || "";
        }}
        options={orderedOptions}
        selectOnFocus
        handleHomeEndKeys
        renderOption={(props, option) => (
          <li {...props}>
            <div
              style={{
                marginLeft: `${(optionDepthMap.get(option._id || "") || 0) * 16}px`,
                width: "100%",
              }}
            >
              {option.name}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            fullWidth={fullWidth}
            placeholder={
              isClientAutoComplete
                ? "Select Client (Or Add New)"
                : t("Select Lab (Or Add New)")
            }
            size="small"
            sx={{
              minWidth: "200px",
              "& .MuiInputBase-root": {
                py: `${py}px !important`,
              },
            }}
            InputProps={{
              ...params.InputProps,
              onClick: (e) => {
                (params.InputProps as any)?.onClick?.(e as React.MouseEvent<HTMLInputElement>);
                if (useModalPicker) {
                  if (eventIsFromAutocompleteClear(e)) {
                    return;
                  }
                  setModalSearchTerm(inputValue);
                  setPickerModalOpen(true);
                }
              },
            }}
          />
        )}
      />
      <Dialog
        open={pickerModalOpen}
        onClose={handleClosePickerModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pr: 1,
          }}
        >
          {label || (isClientAutoComplete ? "Select client" : "Select Company")}
          <Box sx={{ flex: 1 }} />
          <IconButton
            aria-label="close"
            size="small"
            onClick={handleClosePickerModal}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 1,
            maxHeight: "70vh",
          }}
        >
          <SimpleSearchInput
            value={modalSearchTerm}
            onChange={(e) => setModalSearchTerm(e.target.value)}
            size="small"
            placeholder={t("Search Company")}
            onClear={() => setModalSearchTerm("")}
            fullWidth
          />
          <Box sx={{ overflow: "auto", flex: 1 }}>
            {pickerTreeData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No companies match your search.
              </Typography>
            ) : (
              pickerTreeData.map((node) => renderPickerRow(node))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePickerModal}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setPickerModalOpen(false);
              setTimeout(() => {
                toggleOpen(true);
                setDialogValue({
                  name: modalSearchTerm || inputValue || "",
                });
              }, 0);
            }}
          >
            {isClientAutoComplete ||
            ![UserRoles.adminStaff, UserRoles.superadmin].includes(
              loggedInUserInfo?.role || UserRoles.user
            )
              ? t("Add New Client")
              : t("Add New Lab")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {isClientAutoComplete ||
          ![UserRoles.adminStaff, UserRoles.superadmin].includes(
            loggedInUserInfo?.role || UserRoles.user
          )
            ? "Add a new client"
            : "Add a new lab"}
        </DialogTitle>
        <DialogContent sx={{ minWidth: "300px" }}>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            value={dialogValue.name}
            onChange={(event) =>
              setDialogValue({
                ...dialogValue,
                name: event.target.value,
              })
            }
            label={isClientAutoComplete ? "Client Company Name" : "Lab Name"}
            type="text"
            fullWidth
            size="small"
          />
          {isClientAutoComplete ? (
            <FormControlLabel
              disabled={
                isClientAutoComplete ||
                ![UserRoles.adminStaff, UserRoles.superadmin].includes(
                  loggedInUserInfo?.role || UserRoles.user
                )
              }
              control={
                <Checkbox
                  checked={isNewCompanyMyClient}
                  onChange={(event) =>
                    setIsNewCompanyMyClient(event.target.checked)
                  }
                />
              }
              label={`This is <${definedVendor?.name}>'s client`}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <ProgressButton
            onClick={handleSubmit}
            title="Add"
            inProgress={creatingNewCompany}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
